import { useApolloClient, useSubscription } from '@apollo/client/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'es-toolkit';
import type { ProcessingStatus } from '@/graphql';
import { DocumentStatusUpdatedDocument, MyDocumentStatsDocument } from '@/graphql';
import { useAuth } from '@/features/auth';
import { captureEvent, isAnalyticsEnabled, setPersonProperties } from '@/services/analytics';

/** Statuses that mean the extraction itself succeeded. */
const EXTRACTION_SUCCESS_STATUSES = new Set<ProcessingStatus>(['COMPLETED', 'EXTRACTED']);

/** A batch upload finishes many documents at once — one count query per burst is enough. */
const EXTRACTION_COUNT_SYNC_DEBOUNCE_MS = 1000;

/**
 * Tracks successful document extractions in PostHog: sends `document_extracted`
 * and keeps the `extraction_count` person property in sync with the backend.
 */
export function useExtractionTracking(): void {
  const client = useApolloClient();
  const { user } = useAuth();
  const userId = user?.id;

  // A document reports EXTRACTED first and COMPLETED once the invoice is created —
  // both are successes, so the document ID decides whether it was already tracked.
  const trackedDocumentIdsRef = useRef<Set<string>>(new Set());

  const syncExtractionCount = useCallback(async () => {
    if (!userId || !isAnalyticsEnabled()) return;

    try {
      const { data } = await client.query({
        query: MyDocumentStatsDocument,
        variables: { userId },
        fetchPolicy: 'network-only'
      });

      const extractionCount = (data?.documentStats ?? [])
        .filter(stat => EXTRACTION_SUCCESS_STATUSES.has(stat.status))
        .reduce((total, stat) => total + stat.count, 0);

      setPersonProperties({ extraction_count: extractionCount });
    } catch {
      // Analytics is best-effort — a failed count sync must not surface to the user.
    }
  }, [client, userId]);

  const debouncedSyncExtractionCount = useMemo(
    () => debounce(() => void syncExtractionCount(), EXTRACTION_COUNT_SYNC_DEBOUNCE_MS),
    [syncExtractionCount]
  );

  // Drop a pending sync on sign-out/unmount so a stale count never lands on the
  // wrong person; the mount sync below re-establishes it next time.
  useEffect(() => () => debouncedSyncExtractionCount.cancel(), [debouncedSyncExtractionCount]);

  // Already-tracked IDs belong to the identified person only.
  useEffect(() => {
    trackedDocumentIdsRef.current = new Set();
  }, [userId]);

  // Publish the authoritative count for the freshly identified person, so the
  // property exists for targeting even before their next extraction.
  useEffect(() => {
    void syncExtractionCount();
  }, [syncExtractionCount]);

  // onData runs once per message — reading `data` in an effect would collapse
  // documents finishing in the same tick (batch upload) into the last one only.
  useSubscription(DocumentStatusUpdatedDocument, {
    skip: !userId,
    onData: ({ data }) => {
      const updated = data.data?.documentStatusUpdated;
      if (!updated || !userId) return;

      // The subscription is account-wide — count only what this user uploaded.
      // uploadedBy.id is a User Node ID; document.userId is encoded under the Document type.
      if (updated.uploadedBy?.id !== userId) return;
      if (!EXTRACTION_SUCCESS_STATUSES.has(updated.status)) return;
      if (trackedDocumentIdsRef.current.has(updated.id)) return;

      trackedDocumentIdsRef.current.add(updated.id);

      captureEvent('document_extracted', {
        document_id: updated.id,
        document_type: updated.documentType,
        status: updated.status,
        mime_type: updated.file?.mimeType,
        file_size_bytes: updated.file?.sizeBytes,
        reliability_score: updated.qualityMetrics?.reliabilityScore,
        overall_confidence: updated.qualityMetrics?.overallConfidence
      });

      debouncedSyncExtractionCount();
    }
  });
}
